import { Box, Circle, Flex, Text } from "@chakra-ui/react";
import * as React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { HiddenSection, RadioGroup, TextField } from "@/components/Form";
import { FormLabels } from "@/types";
import { generateDynamicFormFields } from "@/utils/generateDynamicFormFields";

import { EgressLight } from "../../types";

type EgressLightsProps = {
  egressLightCount: number;
  labels: FormLabels<EgressLight>;
  isEdit?: boolean;
};

type EgressLightsData = {
  egressLights: EgressLight[];
};

export const EgressLights = React.memo(
  ({ egressLightCount, labels, isEdit = false }: EgressLightsProps) => {
    const { register, control, getFieldState, watch } =
      useFormContext<EgressLightsData>();
    const { fields, append, remove } = useFieldArray({
      name: "egressLights",
      control,
    });

    const egressLights = watch("egressLights");

    React.useLayoutEffect(() => {
      if (egressLightCount > 0 && !isEdit) {
        remove();
        append(
          generateDynamicFormFields<EgressLight>(egressLightCount, {
            egressLightChecked: "",
            egressLightIssues: "",
            egressLightIssueDescription: "",
          }),
        );
      }
    }, [egressLightCount, remove, append, isEdit]);

    if (egressLightCount < 1) {
      return null;
    }

    return fields.map((field, index) => (
      <Box marginY="1rem" key={field.id}>
        <Flex alignItems="flex-start">
          <Circle
            marginRight="1rem"
            size="40px"
            bg="primary.500"
            color="white"
            fontWeight="bold"
          >
            {index + 1}
          </Circle>
          <Box marginTop=".5rem">
            <Text as="b" textDecoration="underline">
              Egress Light {index + 1}
            </Text>
            <RadioGroup
              label={labels.egressLightChecked}
              options={["Yes", "No"]}
              name={`egressLights.${index}.egressLightChecked`}
              control={control}
              error={
                getFieldState(`egressLights.${index}.egressLightChecked`).error
              }
            />
            <RadioGroup
              label={labels.egressLightIssues}
              options={["Yes", "No"]}
              name={`egressLights.${index}.egressLightIssues`}
              control={control}
              error={
                getFieldState(`egressLights.${index}.egressLightIssues`).error
              }
            />
            <HiddenSection
              show={egressLights[index].egressLightIssues === "No"}
            >
              <TextField
                label={labels.egressLightIssueDescription}
                registration={register(
                  `egressLights.${index}.egressLightIssueDescription`,
                )}
                error={
                  getFieldState(
                    `egressLights.${index}.egressLightIssueDescription`,
                  ).error
                }
              />
            </HiddenSection>
          </Box>
        </Flex>
      </Box>
    ));
  },
);
